@let entityId = entityId$ | async;
@let hideControls = hideControls$ | async;

@let isBlockHidden = isHiddenOnView();

@let isActive = entityId === entity().id;
@let isFocus = hoverState()?.hover || isActive || isSettingsMenuOpened();

<div
    #blockRoot
    class="block"
    [id]="displayId()"
    [attr.data-raw-id]="displayId()"
    [class.active]="isActive"
    [class.active-more-action]="isSettingsMenuOpened()"
    [class.hidden-state]="isBlockHidden"
    [webSingleHover]="{ threshold: 100 }"
    (hovered)="hovered($event)"
    #zeroWrapper
>
    @if (isFocus) {
        <div class="info-label-container">
            @if (isPinnedTop() || isPinnedBottom()) {
                <div class="info-label" [tooltip]="tooltip" [tooltipAppendToBody]="true">
                    <img
                        src="./assets/img/websites/lpc-pinned-block-label-icon.svg"
                        [alt]="'control_block_pinned' | translate"
                    />
                    <ng-template #tooltip>
                        {{ "control_block_pinned" | translate }}
                    </ng-template>
                </div>
            }

            @if (hidden().desktop || hidden().tablet || hidden().mobile) {
                <div class="info-label" [tooltip]="hideTooltip" [tooltipAppendToBody]="true">
                    <img [src]="hiddenIconPath()" [alt]="hiddenIconTooltip() | translate" />
                    <ng-template #hideTooltip>
                        {{ hiddenIconTooltip() | translate }}
                    </ng-template>
                </div>
            }
        </div>
    }

    @if (!isBlockHidden) {
        <!--
            One binding per custom property, not one bag: each reads a single node
            of the signal form, so a height edit rewrites one declaration rather
            than thirty.
        -->
        <div
            class="block-wrap zero-block-wrap"
            [style.--block-max-width]="maxWidth()"
            [style.--block-height]="blockHeight()"
            [style.--block-bg-color]="bgColor()"
            [style.--block-bg-image]="bgImage()"
            [style.--block-bg-position]="bgPosition()"
            [style.--block-bg-repeat]="bgRepeat()"
            [style.--block-bg-size]="bgSize()"
            [style.--block-bg-filter]="bgFilter()"
            [style.--block-border-top]="borderTop()"
            [style.--block-border-right]="borderRight()"
            [style.--block-border-bottom]="borderBottom()"
            [style.--block-border-left]="borderLeft()"
            [style.--block-height-tablet]="blockHeightTablet()"
            [style.--block-bg-color-tablet]="bgColorTablet()"
            [style.--block-bg-image-tablet]="bgImageTablet()"
            [style.--block-bg-position-tablet]="bgPositionTablet()"
            [style.--block-bg-repeat-tablet]="bgRepeatTablet()"
            [style.--block-bg-size-tablet]="bgSizeTablet()"
            [style.--block-bg-filter-tablet]="bgFilterTablet()"
            [style.--block-border-top-tablet]="borderTopTablet()"
            [style.--block-border-right-tablet]="borderRightTablet()"
            [style.--block-border-bottom-tablet]="borderBottomTablet()"
            [style.--block-border-left-tablet]="borderLeftTablet()"
            [style.--block-height-mobile]="blockHeightMobile()"
            [style.--block-bg-color-mobile]="bgColorMobile()"
            [style.--block-bg-image-mobile]="bgImageMobile()"
            [style.--block-bg-position-mobile]="bgPositionMobile()"
            [style.--block-bg-repeat-mobile]="bgRepeatMobile()"
            [style.--block-bg-size-mobile]="bgSizeMobile()"
            [style.--block-bg-filter-mobile]="bgFilterMobile()"
            [style.--block-border-top-mobile]="borderTopMobile()"
            [style.--block-border-right-mobile]="borderRightMobile()"
            [style.--block-border-bottom-mobile]="borderBottomMobile()"
            [style.--block-border-left-mobile]="borderLeftMobile()"
        >
            <web-block-shape-dividers [entity]="entity()" />

            @if (!isParallax()) {
                <div class="block-bg" [class.has-fixed-bg]="isFixedBg()">
                    @if (desktopVideoUrl()) {
                        <video
                            class="desktop-video"
                            style="width: 100%; height: 100%; object-fit: cover"
                            [autoplay]="true"
                            [muted]="true"
                            [loop]="true"
                            [src]="desktopVideoUrl()"
                        ></video>
                    }
                    @if (tabletVideoUrl()) {
                        <video
                            class="tablet-video"
                            style="width: 100%; height: 100%; object-fit: cover"
                            [autoplay]="true"
                            [muted]="true"
                            [loop]="true"
                            [src]="tabletVideoUrl()"
                        ></video>
                    }
                    @if (mobileVideoUrl()) {
                        <video
                            class="mobile-video"
                            style="width: 100%; height: 100%; object-fit: cover"
                            [autoplay]="true"
                            [muted]="true"
                            [loop]="true"
                            [src]="mobileVideoUrl()"
                        ></video>
                    }
                </div>
            } @else {
                <div class="parallax-container">
                    <div class="background-parallax">
                        <div class="parallax-wrapper">
                            <div class="parallax">
                                <div class="block-bg background has-parallax"></div>
                            </div>
                        </div>
                    </div>
                </div>
            }

            <div class="block-inner">
                @for (section of entity().children; track section.id; let i = $index) {
                    <web-zero-section
                        [index]="i"
                        [entity]="section"
                        [parentEntityId]="entity().id"
                        [hasSectionActiveColumn]="hasSectionActiveColumn$(section) | async"
                        [isLastSection]="false"
                    />
                }
            </div>
        </div>
    }

    @if (hideControls) {
        @if (!isPinnedTop() && hoverState()?.topThreshold) {
            <web-add-buttons
                type="top"
                [show]="true"
                [tooltipText]="'control_add_block' | translate"
                (addEvent)="addBlockTop()"
            />
        }
        @if (!isPinnedBottom() && !isBlockHidden && hoverState()?.bottomThreshold) {
            <web-resizable-add-buttons
                type="bottom"
                [show]="true"
                [height]="height()"
                [tooltipText]="'control_add_block' | translate"
                (addEvent)="addBlockBottom()"
                (resizeEvent)="blockHeightChange($event)"
                (resizeStartEvent)="onHeightResizeStart()"
                (resizeEndEvent)="onHeightResizeEnd()"
            />
        }

        @if (isFocus) {
            @let isAdmin = isAdmin$ | async;
            <web-zero-block-controls
                class="block-controls"
                [disableMoveUp]="isFirst() || isPreviousSiblingPinnedToTop() || isPreviousSiblingShared()"
                [disableMoveDown]="isLast() || isNextSiblingPinnedToBottom() || isNextSiblingShared()"
                [selectActionTooltip]="'control_block_settings' | translate"
                [pinOnTheTop]="isPinnedTop()"
                [pinOnTheBottom]="isPinnedBottom()"
                [saveBlockToGallery]="!isAdmin"
                (copyEvent)="copyBlock()"
                (saveBlockToGalleryEvent)="saveBlockToGallery()"
                (deleteEvent)="removeBlock()"
                (selectEvent)="selectBlock()"
                (moveUpEvent)="moveUp()"
                (moveDownEvent)="moveDown()"
                (toggleMoreActionState)="setSettingsMenuOpened($event)"
            />
        }
    }

    @if (!isBlockHidden) {
        <div class="zero-block-content-boundary">
            @for (_ of columnLines(); track $index) {
                <div class="column-line"></div>
            }
        </div>
        @if (hoverState()?.hover) {
            <div
                #addWidgetBtn
                class="add-widget-btn"
                [class.tablet]="isDeviceTablet()"
                [class.mobile]="isDeviceMobile()"
            >
                <button class="btn btn-green" (click)="addWidget()">
                    <i class="sp-icon icon-plus-add mr-0"> </i>
                </button>
            </div>
        }
    } @else {
        <div class="hidden-block-placeholder">
            {{ hiddenBlockHintText() | translate }}
        </div>
    }
</div>
